import { Box, Divider, Chip } from "@mui/material";
import Image from "next/image";
import { useContext, useEffect, useState } from "react";
import { useUserData } from "src/hooks/useUserData";
import { useMessages } from "src/hooks/useMessages";
import { db } from "../api_config/firebase-configuration";
import { doc, setDoc } from "firebase/firestore";
import { compressImage, uploadToFbStorage } from "src/lib/global";
import MessageItem from "src/components/Chat/MessageItem";
import MessageFile from "src/components/Chat/MessageFile";
import MessageImage from "src/components/Chat/MessageImage";
import { LocalizationContext } from "../src/lib/context";

export default function Chat() {
  const { chatPage } = useContext(LocalizationContext).localeStrings;
  const [newMessage, setNewMessage] = useState<string>(() => "");
  const { user, userData } = useUserData();
  const { messages, loading } = useMessages();
  const [selectedChannel, setSelectedChannel] = useState<string>("Általános");

  // scroll to the bottom of the chat when new message comes or is sent
  useEffect(() => {
    const scrollingElement = document.getElementById("messages") as HTMLElement;
    scrollingElement.scrollTop = scrollingElement.scrollHeight;
  }, [messages]);

  // Send message, file and image
  const sendHandler = async (file: any) => {
    if (!newMessage && !file) {
      alert("Please type message");
      return;
    }

    // 1. First, we need to determine if the user is sending text message or file or image
    // User is sending text message
    let updatedMessages: any;
    if (newMessage) {
      setNewMessage("");
      // If user has old messages, we merge it with new message, If not, we just save the new and first message
      const newMessageObject = {
        message: newMessage,
        fromClient: true,
        messageSentTime: new Date().getTime(),
        problem: selectedChannel,
        type: "text",
      };
      updatedMessages = messages
        ? [...messages, newMessageObject]
        : [newMessageObject];
    }

    // User is sending file
    if (file && file.type === "application/pdf") {
      const url = await uploadToFbStorage(file, `files/${file.name}`);

      const newFileObject = {
        messageSentTime: new Date().getTime(),
        fileUrl: url,
        fromClient: true,
        fileName: file.name,
        size: Math.floor(file.size / 1000),
        type: "file",
      };
      updatedMessages = messages
        ? [...messages, newFileObject]
        : [newFileObject];
    }

    // User is sending image
    if (file && file.type.includes("image")) {
      const compressedImg = await compressImage(file, 450);
      if (compressedImg) {
        const url = await uploadToFbStorage(
          compressedImg,
          `messageImages/${file.name}`
        );
        const newFileObject = {
          messageSentTime: new Date().getTime(),
          imageUrl: url,
          fromClient: true,
          type: "image",
        };
        updatedMessages = messages
          ? [...messages, newFileObject]
          : [newFileObject];
      }
    }

    await setDoc(
      doc(db, "chat", userData.uid),
      { messages: updatedMessages },
      { merge: true }
    );
  };

  const getChannels = () => {
    const problemTypes = ["Általános"];
    if (userData?.subscriptionLevel)
      problemTypes.push(userData.subscriptionLevel);
    return problemTypes;
  };

  const channelMessages = () => {
    return messages?.filter(
      (message: any) => message.problem === selectedChannel
    );
  };

  return (
    <div className="chat">
      <div className="chat__upper">
        <div className="chat__upper--content">
          <h2>{chatPage.bannerTitle}</h2>
          <Image src="/assets/droneImage.png" alt="" width="350" height="150" />
        </div>
      </div>

      <div className="chat__main">
        <div className="chat__main--header">
          <h2>{chatPage.title}</h2>
          <h6>{chatPage.subtitle}</h6>

          <div className="chat__main--channels">
            {getChannels().map((channel, i) => (
              <Chip
                label={channel}
                key={i}
                color={selectedChannel === channel ? "primary" : "default"}
                variant="outlined"
                onClick={() => setSelectedChannel(channel)}
              />
            ))}
          </div>
        </div>
        <Divider sx={{ margin: "28px 0" }} />

        <div className="messages" id="messages">
          {channelMessages() &&
            channelMessages().map((item: any, i: number) => {
              switch (item.type) {
                case "text":
                  return <MessageItem {...item} userData={userData} key={i} />;
                case "file":
                  return <MessageFile {...item} userData={userData} key={i} />;
                case "image":
                  return <MessageImage {...item} userData={userData} key={i} />;
              }
            })}

          {!messages && !loading && (
            <Box sx={{ textAlign: "center" }}>
              <h3>{chatPage.messagesPlaceholder}</h3>
            </Box>
          )}

          {loading && userData && (
            <Box sx={{ textAlign: "center" }}>
              <h3>{chatPage.loading}</h3>
            </Box>
          )}

          {!user && (
            <Box sx={{ textAlign: "center" }}>
              <h3>{chatPage.logInToSend}</h3>
            </Box>
          )}
        </div>

        {user && (
          <div className="chat__bottom">
            <div className="chat__bottom--left">
              <label htmlFor="file">
                <Image
                  src="/assets/select-file.png"
                  alt=""
                  width="24"
                  height="24"
                />
              </label>
              <input
                type="file"
                id="file"
                accept=".pdf"
                onChange={(e: any) =>
                  userData && sendHandler(e.target.files[0])
                }
              />
              <label htmlFor="photo">
                <Image
                  src="/assets/gallery-add.png"
                  alt=""
                  width="24"
                  height="24"
                />
              </label>
              <input
                type="file"
                id="photo"
                accept="image/png, image/jpeg"
                onChange={(e: any) =>
                  userData && sendHandler(e.target.files[0])
                }
              />
            </div>
            <div className="chat__bottom--input">
              <input
                type="text"
                value={newMessage}
                placeholder={chatPage.textFieldPlaceholder}
                onChange={(e) => setNewMessage(e.target.value)}
              />
            </div>
            <button
              className="chat__bottom--button"
              onClick={() => userData && sendHandler("")}
            >
              {chatPage.sendButton}{" "}
              <Image
                src="/assets/telegramIcon.png"
                alt=""
                width="30"
                height="30"
              />
            </button>
          </div>
        )}
      </div>

      {/* <BottomInfo textsArray={[]} headerText="About us" /> */}
    </div>
  );
}
